<template>
  <v-dialog
    transition="dialog-bottom-transition"
    scrollable
    persistent
    max-width="500"
    v-model="dialogModel"
  >
    <v-card v-if="adhocRevenue" class="adhoc-revenue-dialog-card">
      <v-card-title class="headline grey lighten-2 text-xs-center" primary-title>
        Submit Ad Hoc Revenue
      </v-card-title>
      <v-card-text class="pa-2 adhoc-revenue-dialog-body">
        <currency-field
          pb-2
          v-model="adhocRevenue.amount"
          @blur="(val) => adhocRevenue.amount = val"
          :child-props="{ label: 'Amount', disabled: addingAdhocRevenue }"
        />
        <st-date-field
          pb-2
          label="Transaction Date"
          v-model="adhocRevenue.dateCreated"
          :disabled="addingAdhocRevenue"
        />
        <v-text-field
          v-if="isVehicleLocked"
          pb-2
          label="Vehicle"
          :value="lockedVehicle.licensePlateNumber"
          readonly
          disabled
        />
        <v-autocomplete
          v-else
          v-model="adhocRevenue.transportationProfileId"
          :items="vehicles"
          :loading="vehiclesApiInProgress"
          :search-input.sync="adHocVehicleKeywords"
          hide-no-data
          hide-selected
          hide-details
          item-text="licensePlateNumber"
          item-value="id"
          label="Vehicle"
          placeholder="Search by licence plate"
          prepend-icon="fal fa-bus"
          outline
          clearable
        />
        <v-switch
          class="mb-3"
          style="margin-top: 0"
          :disabled="addingAdhocRevenue || !adhocRevenue.dateCreated || !adhocRevenue.transportationProfileId"
          v-model="deductAgentTopups"
          label="Deduct agent top-ups"
          hide-details
        />
        <v-text-field
          pb-2
          :disabled="addingAdhocRevenue"
          v-model="adhocRevenue.notes"
          label="Notes"
        />
        <div class="mb-2">
          <v-text-field
            :disabled="addingAdhocRevenue"
            v-model="adhocRevenue.confirmationId"
            label="Confirmation ID"
            persistent-hint
            hint="(Optional) Can be bank deposit slip number, mobile money transaction ID, etc."
          />
        </div>
        <div v-if="deductAgentTopups" class="mb-3 topup-results-panel">
          <v-progress-linear
            v-if="agentTopupsApiInProgress"
            indeterminate
            color="primary"
            class="mb-2"
          />
          <v-alert
            v-else-if="!agentTopups.length"
            type="info"
            outlined
            dense
            class="mb-2"
          >
            No agent top-ups found for this bus on this date.
          </v-alert>
          <div v-else>
            <div class="d-flex align-center justify-space-between mb-2">
              <v-checkbox
                hide-details
                :input-value="allAgentTopupsSelected"
                :indeterminate="!allAgentTopupsSelected && anyAgentTopupsSelected"
                label="Select all"
                class="mt-0 pt-0"
                @change="toggleAllAgentTopups"
              />
              <div class="caption grey--text text--darken-1">
                Selected {{ selectedAgentProfileIds.length }} of {{ agentTopups.length }} - deducting {{ formatAmount(agentTopupDeductionTotal) }}
              </div>
            </div>
            <v-divider class="mb-2" />
            <div class="topup-list">
              <div
                v-for="agentTopup in agentTopups"
                :key="agentTopup.agentProfileId"
                class="d-flex align-center justify-space-between mb-1"
              >
                <v-checkbox
                  hide-details
                  :input-value="selectedAgentProfileIds.includes(agentTopup.agentProfileId)"
                  class="mt-0 pt-0 mr-2"
                  @change="toggleAgentTopupSelection(agentTopup.agentProfileId)"
                />
                <div class="flex-grow-1">
                  <div class="body-2 font-weight-medium">
                    {{ agentTopup.fullName }}
                  </div>
                  <div class="caption grey--text text--darken-1">
                    {{ agentTopup.agentPhone || 'No phone' }} - {{ agentTopup.mostLikelyTicketCount || 0 }} tickets on this bus
                  </div>
                </div>
                <div class="body-2 font-weight-medium text-xs-right">
                  {{ formatAmount(agentTopup.netPassengerCredits) }}
                </div>
              </div>
            </div>
            <v-divider class="my-2" />
            <div class="body-2 topup-summary-row">
              <span class="topup-summary-label">Entered</span>
              <span class="topup-summary-value">{{ formatAmount(adhocRevenue.amount) }}</span>
            </div>
            <div class="body-2 topup-summary-row">
              <span class="topup-summary-label">Total deductions</span>
              <span class="topup-summary-value">{{ formatAmount(agentTopupDeductionTotal) }}</span>
            </div>
            <div class="subtitle-1 font-weight-bold topup-summary-row">
              <span class="topup-summary-label">Net to submit</span>
              <span class="topup-summary-value">{{ formatAmount(finalAmountToSubmit) }}</span>
            </div>
          </div>
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn color="warning" :disabled="addingAdhocRevenue" flat @click="closeDialog">
          Cancel
        </v-btn>
        <v-spacer />
        <v-btn color="primary" :disabled="addingAdhocRevenue || !adhocRevenue.amount || !adhocRevenue.dateCreated || !adhocRevenue.transportationProfileId || (deductAgentTopups && !selectedAgentProfileIds.length) || agentTopupsApiInProgress" :loading="addingAdhocRevenue" @click="saveAdhocRevenue">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import src from './src'
export default src
</script>

<style scoped>
.adhoc-revenue-dialog-card {
  background: #fff !important;
  opacity: 1 !important;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}

.adhoc-revenue-dialog-body {
  background: #fff !important;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
}

.topup-results-panel {
  background: #fff;
}

.topup-list {
  max-height: 200px;
  overflow-y: auto;
  padding-right: 6px;
}

.topup-summary-row {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  column-gap: 12px;
}

.topup-summary-value {
  min-width: 120px;
  text-align: right;
}
</style>
